import { useState } from "react";
import { useGlobalContext } from "../GlobalContext";
import { graphqlRequest } from "../Graphql/graphqlClient";
import {
  REPORTS_GET_DRIVER_ANALYTICS,
  REPORTS_GET_DRIVER_REPORT,
  REPORTS_GET_TASK_ANALYTICS,
  REPORTS_GET_TASK_ANALYTICS_BY_STATUS,
} from "../Graphql/queries/reportsQueries";
import { toast } from "sonner";

const useReports = () => {
  const {
    api,
    AdminId,
    apiGraphqlUrl,
    GraphToken,
    handleApiResponseCode,
  } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [dailyPointsLoading, setDailyPointsLoading] = useState(false);
  const [masterPointsLoading, setMasterPointsLoading] = useState(false);
  const [weeklyPointsLoading, setWeeklyPointsLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "01" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const handleGraphqlApiResponse = (responseData) => {
    if (!responseData) return false;

    if (
      responseData?.responseCode === "02" ||
       responseData?.responseCode === "03" ||
      responseData?.responseCode === "98" ||
      responseData?.responseCode === "401"
    ) {
      handleApiResponseCode?.(responseData);
      return true;
    }

    return false;
  };

  const fetchPackkyData = async (
    fromDate,
    toDate,
    setRestaurantData,
    setDriverTaskAnalytics,
    setTaskStatusAnalytics
  ) => {
    setLoading(true);

    try {
      if (!apiGraphqlUrl || !GraphToken) {
        toast.error("GraphQL configuration missing for reports analytics");
        setRestaurantData([]);
        setDriverTaskAnalytics([]);
        setTaskStatusAnalytics([]);
        return;
      }

      const variables = {
        req: {
          adminId: AdminId || "",
          fromDate,
          toDate,
        },
      };

      const [
        taskAnalyticsGraphRes,
        driverAnalyticsGraphRes,
        statusAnalyticsGraphRes,
      ] = await Promise.all([
        graphqlRequest({
          url: apiGraphqlUrl,
          query: REPORTS_GET_TASK_ANALYTICS,
          variables,
          token: GraphToken,
          authType: "Basic",
        }),

        graphqlRequest({
          url: apiGraphqlUrl,
          query: REPORTS_GET_DRIVER_ANALYTICS,
          variables,
          token: GraphToken,
          authType: "Basic",
        }),

        graphqlRequest({
          url: apiGraphqlUrl,
          query: REPORTS_GET_TASK_ANALYTICS_BY_STATUS,
          variables,
          token: GraphToken,
          authType: "Basic",
        }),
      ]);

      const taskAnalyticsResponse = taskAnalyticsGraphRes?.taskGetTaskAnalytics;
      const driverAnalyticsResponse =
        driverAnalyticsGraphRes?.driverGetDriverAnalytics;
      const statusAnalyticsResponse =
        statusAnalyticsGraphRes?.taskGetTaskAnalyticsByStatus;

      const hasBlockedResponse =
        handleGraphqlApiResponse(taskAnalyticsResponse) ||
        handleGraphqlApiResponse(driverAnalyticsResponse) ||
        handleGraphqlApiResponse(statusAnalyticsResponse);

      if (hasBlockedResponse) {
        setRestaurantData([]);
        setDriverTaskAnalytics([]);
        setTaskStatusAnalytics([]);
        return;
      }

      const isTaskAnalyticsSuccess =
        taskAnalyticsResponse?.responseCode === "00" ||
        taskAnalyticsResponse?.responseCode === "01";

      const isDriverAnalyticsSuccess =
        driverAnalyticsResponse?.responseCode === "00" ||
        driverAnalyticsResponse?.responseCode === "01";

      const isStatusAnalyticsSuccess =
        statusAnalyticsResponse?.responseCode === "00" ||
        statusAnalyticsResponse?.responseCode === "01";

      const taskAnalyticsData = Array.isArray(taskAnalyticsResponse?.data)
        ? taskAnalyticsResponse.data.map((shop) => ({
            ...shop,
            id: shop?.id,
            taskCount: Number(shop?.taskCount || 0),
            shopCode: shop?.shopCode || "—",
            shopName: shop?.shopName || "Unknown Shop",
          }))
        : [];

      const driverAnalyticsData = Array.isArray(driverAnalyticsResponse?.data)
        ? driverAnalyticsResponse.data.map((driver) => ({
            ...driver,
            id: driver?.id,
            taskCount: Number(driver?.taskCount || 0),
            driverCode: driver?.driverCode || "—",
            driverName: driver?.driverName || "Unknown Driver",
            driverMobile: driver?.driverMobile || "—",
          }))
        : [];

      const statusAnalyticsData = Array.isArray(statusAnalyticsResponse?.data)
        ? statusAnalyticsResponse.data.map((status) => ({
            ...status,
            id: status?.id,
            taskCount: Number(status?.taskCount || 0),
            taskStatus: status?.taskStatus || "unknown",
          }))
        : [];

      if (isTaskAnalyticsSuccess) {
        setRestaurantData(taskAnalyticsData);
      } else {
        setRestaurantData([]);

        if (shouldShowLocalError(taskAnalyticsResponse)) {
          toast.error(
            taskAnalyticsResponse?.responseMessage ||
              "Failed to fetch task analytics"
          );
        }
      }

      if (isDriverAnalyticsSuccess) {
        setDriverTaskAnalytics(driverAnalyticsData);
      } else {
        setDriverTaskAnalytics([]);

        if (shouldShowLocalError(driverAnalyticsResponse)) {
          toast.error(
            driverAnalyticsResponse?.responseMessage ||
              "Failed to fetch driver analytics"
          );
        }
      }

      if (isStatusAnalyticsSuccess) {
        setTaskStatusAnalytics(statusAnalyticsData);
      } else {
        setTaskStatusAnalytics([]);

        if (shouldShowLocalError(statusAnalyticsResponse)) {
          toast.error(
            statusAnalyticsResponse?.responseMessage ||
              "Failed to fetch status analytics"
          );
        }
      }
    } catch (err) {
      console.error("Error loading reports:", err);

      setRestaurantData([]);
      setDriverTaskAnalytics([]);
      setTaskStatusAnalytics([]);

      toast.error(err?.message || "Error loading reports");
    } finally {
      setLoading(false);
    }
  };

  // ================= SINGLE DRIVER ANALYTICS =================
  const fetchDriverAnalytics = async (
    driverCode,
    fromDate,
    toDate,
    setAnalytics
  ) => {
    setLoading(true);

    try {
      const payload = {
        adminId: AdminId,
        driverCode,
        fromDate,
        toDate,
      };

      const res = await api.post("/api/Driver/v2.0/getDriverAnalytics", payload);

      if (res?.data?.responseCode === "00") {
        setAnalytics(res.data?.data || null);
        return res.data?.data || null;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch driver analytics"
        );
      }

      setAnalytics(null);
      return null;
    } catch (error) {
      console.error("Analytics API Error:", error);
      setAnalytics(null);
      toast.error("Error fetching driver analytics");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ================= SINGLE DRIVER REPORTS - GRAPHQL =================
  const fetchDriverReports = async (
    driverCode,
    fromDate,
    toDate,
    setReports
  ) => {
    setTaskLoading(true);

    try {
      if (!apiGraphqlUrl || !GraphToken) {
        toast.error("GraphQL configuration missing for driver reports");
        setReports([]);
        return;
      }

      const variables = {
        req: {
          adminId: AdminId || "",
          driverCode,
          fromDate,
          toDate,
        },
      };

      const graphRes = await graphqlRequest({
        url: apiGraphqlUrl,
        query: REPORTS_GET_DRIVER_REPORT,
        variables,
        token: GraphToken,
        authType: "Basic",
      });

      const reportResponse = graphRes?.taskGetDriverReport;

      if (handleGraphqlApiResponse(reportResponse)) {
        setReports([]);
        return [];
      }

      const reportData = Array.isArray(reportResponse?.data)
        ? [...reportResponse.data].reverse()
        : [];

      const isReportSuccess =
        reportResponse?.responseCode === "00" ||
        reportResponse?.responseCode === "01";

      if (isReportSuccess) {
        setReports(reportData);
        return reportData;
      }

      if (shouldShowLocalError(reportResponse)) {
        toast.error(
          reportResponse?.responseMessage || "Failed to fetch driver reports"
        );
      }

      setReports([]);
      return [];
    } catch (err) {
      console.error("Driver reports GraphQL error:", err);
      setReports([]);
      toast.error(err?.message || "Error fetching driver reports");
      return [];
    } finally {
      setTaskLoading(false);
    }
  };

  // ================= DRIVER WEEKLY PERFORMANCE POINTS =================
  const fetchDriverPerformancePoints = async (driverId, setPoints) => {
    setPointsLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getDriverPerformmancePoints", {
        driverId,
      });

      if (res?.data?.responseCode === "00") {
        const data = res.data?.data?.[0] || null;
        setPoints(data);
        return data;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch performance points"
        );
      }

      setPoints(null);
      return null;
    } catch (error) {
      console.error("Driver performance points error:", error);
      setPoints(null);
      toast.error("Error fetching performance points");
      return null;
    } finally {
      setPointsLoading(false);
    }
  };

  // ================= DRIVER DAILY PERFORMANCE POINTS =================
  const fetchDriverDailyPerformancePoints = async (
    driverId,
    fromDate,
    toDate,
    setDailyPoints
  ) => {
    setDailyPointsLoading(true);

    try {
      const payload = {
        driverId,
        fromDate,
        toDate,
      };

      const res = await api.post(
        "/api/Driver/v1.0/getDriverDailyPerformmancePoints",
        payload
      );

      if (res?.data?.responseCode === "00") {
        const data = res.data?.data || [];
        setDailyPoints(data);
        return data;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch daily performance points"
        );
      }

      setDailyPoints([]);
      return [];
    } catch (error) {
      console.error("Driver daily performance points error:", error);
      setDailyPoints([]);
      toast.error("Error fetching daily performance points");
      return [];
    } finally {
      setDailyPointsLoading(false);
    }
  };

  // ================= DRIVER MASTER POINTS SYSTEM =================
  const fetchDriverMasterPointsData = async (setMasterPointsData) => {
    setMasterPointsLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getDriverMasterPointsData", {
        userId: AdminId,
      });

      if (res?.data?.responseCode === "00") {
        const data = res.data?.data || [];
        setMasterPointsData(data);
        return data;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch master points data"
        );
      }

      setMasterPointsData([]);
      return [];
    } catch (error) {
      console.error("Driver master points data error:", error);
      setMasterPointsData([]);
      toast.error("Error fetching master points data");
      return [];
    } finally {
      setMasterPointsLoading(false);
    }
  };

  // ================= DRIVER WEEKLY PERFORMANCE POINTS =================
  const fetchDriverWeeklyPerformancePoints = async (
    driverId,
    fromDate,
    toDate,
    setWeeklyPoints
  ) => {
    setWeeklyPointsLoading(true);

    try {
      const payload = {
        driverId,
        fromDate,
        toDate,
      };

      const res = await api.post(
        "/api/Driver/v1.0/getDriverWeeklyPerformmancePoints",
        payload
      );

      if (res?.data?.responseCode === "00") {
        const data = res.data?.data || [];
        setWeeklyPoints(data);
        return data;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch weekly performance points"
        );
      }

      setWeeklyPoints([]);
      return [];
    } catch (error) {
      console.error("Driver weekly performance points error:", error);
      setWeeklyPoints([]);
      toast.error("Error fetching weekly performance points");
      return [];
    } finally {
      setWeeklyPointsLoading(false);
    }
  };

  return {
    fetchPackkyData,
    fetchDriverAnalytics,
    fetchDriverReports,
    fetchDriverPerformancePoints,
    fetchDriverDailyPerformancePoints,
    fetchDriverMasterPointsData,
    fetchDriverWeeklyPerformancePoints,

    loading,
    taskLoading,
    pointsLoading,
    dailyPointsLoading,
    masterPointsLoading,
    loadingDrivers,
    weeklyPointsLoading,
  };
};

export default useReports;